import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useBrand } from '../../context/BrandContext';
import { useCopy } from '../../context/CopyContext';
import ApiService from '../../services/apiService';
import Header from '../Header';
import Heading from '../Heading';
import Button from '../Button';
import MultiLineCopy from '../MultiLineCopy';
import PoweredBy from '../PoweredBy';
import ItemsModalView from './ItemsModalView';

const AddEmptyView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { brandConfig, isDarkMode } = useBrand();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [isWeightStable, setIsWeightStable] = useState(false);
  const [isCheckingWeight, setIsCheckingWeight] = useState(false);

  // Clear experience tracking when component mounts
  useEffect(() => {
    const clearExperienceTracking = async () => {
      try {
        // Clear the experience tracking variables (items_weight & experience_baseline)
        // baseline_weight is set at the very start and NEVER changes
        console.log('🧹 Clearing experience tracking (items_weight & experience_baseline)...');
        await ApiService.setItemsWeight(null);
        await ApiService.setExperienceBaseline(null);
        console.log('✅ Ready to detect - accumulated weight = current_weight - baseline_weight');
      } catch (error) {
        console.error('Failed to clear experience tracking:', error);
      }
    };
    clearExperienceTracking();
  }, []);

  // Check weight stability every 500ms
  useEffect(() => {
    const interval = setInterval(checkWeightStability, 500);
    return () => clearInterval(interval);
  }, []);

  const checkWeightStability = async () => {
    try {
      // If weight monitoring is disabled, always mark as stable
      if (state.settings.disableWeightMonitoring) {
        setIsWeightStable(true);
        return;
      }

      const response = await ApiService.getWeight();
      if (response.success) {
        // On Add Empty page, we ONLY care about stability (is weight settling?)
        // We don't care about fluctuation warnings here since user is expected to add items
        // is_stable: readings are consistent (not bouncing) - this is what we want
        const isReady = response.is_stable === true;
        setIsWeightStable(isReady);
      } else {
        // Scale not connected or error - don't show "Scale ready"
        setIsWeightStable(false);
      }
    } catch (error) {
      console.error('Failed to check weight stability:', error);
      // If scale is unavailable, don't show "Scale ready"
      setIsWeightStable(false);
    }
  };

  const handleCancel = () => {
    dispatch({ type: 'SET_STEP', payload: 2 });
    navigate('/before-start');
  };

  const handleContinue = async () => {
    setIsCheckingWeight(true);

    // Double check weight is stable before navigating
    try {
      // If weight monitoring is disabled, skip stability check
      if (!state.settings.disableWeightMonitoring) {
        const response = await ApiService.getWeight();
        if (response.success) {
          // Only check stability on Add Empty page - weight changes are expected here
          const isReady = response.is_stable === true;
          if (!isReady) {
            console.warn('Weight not stable yet, cannot continue');
            setIsCheckingWeight(false);
            return;
          }

          // Store the weight with items for tracking
          if (response.current_weight !== undefined) {
            console.log(`📦 Storing items weight: ${response.current_weight}g`);
            await ApiService.setItemsWeight(response.current_weight);
          }
        }

        console.log('✅ Weight stable - proceeding to detection');
      } else {
        console.log('⚖️ Weight monitoring disabled - proceeding to detection');
      }
    } catch (error) {
      console.error('Failed to verify weight stability:', error);
    }

    // Navigate FIRST to avoid fluctuation warning flash
    dispatch({ type: 'SET_STEP', payload: 4 });
    navigate('/checking-empty');

    // Set experience baseline in background
    setTimeout(async () => {
      try {
        const response = await ApiService.getWeight();
        if (response.success && response.current_weight !== undefined) {
          console.log(`🔒 Locking experience baseline to ${response.current_weight}g`);
          await ApiService.setExperienceBaseline(response.current_weight);
        }
      } catch (error) {
        console.error('Failed to set experience baseline:', error);
      }
    }, 100);
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden" data-node-id="73:821">
      {/* Animated Teal Glow Effect at Bottom */}
      <motion.div
        className="absolute bottom-[-675px] left-0  w-[1080px] h-[1080px] rounded-[1000px] pointer-events-none"
        style={{
          background: brandConfig.colors.primary[isDarkMode ? 'dark' : 'light'],
          filter: 'blur(200px)',
        }}
        animate={{
          y: [0, -100, 0],          
          opacity: [.25, .75, .25],
          scale: [1, 1.25, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      {/* Subtle Blur Overlay for Bottom Area */}
      {/* <div 
        className="absolute bottom-0 left-0 right-0 h-[400px] pointer-events-none"
        style={{
          background: 'rgba(255, 255, 255, 0.10)',
          backdropFilter: 'blur(100px)',
        }}
      /> */}

      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header 
              showItems={state.itemsCount > 0} 
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with padding */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 relative mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Rows 1 & 2 - Main heading spanning two rows */}
          <div className="row-span-2 flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading 
                  title={getCopy('addEmpty.heading')}
                  subtitle={getCopy('addEmpty.subheading').replace('{ordinal}', state.itemsCount.toString() === "0" ? "first" : "next")}
                />
              </motion.div>
            </div>
          </div>

          {/* Row 3 - Did you know section + weight status */}
          <div className="flex items-end justify-center">
            <div className="flex flex-col gap-6 items-center">
              <motion.div
                className="text-center w-full max-w-[680px]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <div className="font-brand-secondary font-light leading-none not-italic text-[32px] text-center text-brand-text-primary">
                  <MultiLineCopy
                    text={getCopy('addEmpty.helpText')}
                    className="leading-[42px]"
                  />
                </div>
              </motion.div>

              {/* Weight Status Indicator - HIDDEN FOR DEMO */}
              {/* <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.5 }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  isWeightStable
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                <motion.div
                  className={`w-2 h-2 rounded-full ${
                    isWeightStable ? 'bg-green-500' : 'bg-yellow-500'
                  }`}
                  animate={isWeightStable ? {} : {
                    opacity: [1, 0.3, 1],
                    scale: [1, 1.3, 1],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                  }}
                />
                <span className="text-sm font-medium">
                  {isWeightStable ? 'Scale ready' : 'Waiting for stable weight...'}
                </span>
              </motion.div> */}
            </div>
          </div>
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Buttons */}
          <div className="flex gap-4 justify-center">
            <Button
              variant="secondary"
              onClick={handleCancel}
            >
              {getCopy('addEmpty.buttons.secondary')}
            </Button>
            <Button
              variant="primary"
              onClick={handleContinue}
              className="min-w-[200px]"
              disabled={!isWeightStable || isCheckingWeight}
            >
              Continue
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default AddEmptyView;
